// Modal "Tokens das conexões" — mockup "Modal Tokens" (720px): uma linha por
// conexão com token, com revelar e copiar. O token já vem em GET /whatsapp,
// então o modal não faz requisição própria. Acima da lista fica o token de
// SMS da empresa (um só, independente de conexão), que a página busca em
// GET /sms/api-token e só o admin vê, gera e regera.
import React, { useState } from "react";

import { makeStyles, alpha } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import CircularProgress from "@material-ui/core/CircularProgress";

import AerosDialog from "../../components/AerosDialog";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";
import { MONO } from "./apiPageStyles";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    list: { display: "flex", flexDirection: "column", gap: 8 },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 12,
      padding: "12px 14px",
      // No celular a identidade (190px fixos) + token + ações não cabem numa
      // linha: o token desce para a linha de baixo, inteiro.
      [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
    },
    dot: { width: 8, height: 8, borderRadius: "50%", flex: "none" },
    dotOn: { backgroundColor: AEROS.success },
    dotOff: { backgroundColor: AEROS.error },
    identity: {
      minWidth: 0,
      width: 190,
      flex: "none",
      [theme.breakpoints.down("xs")]: { width: "auto", flex: 1 },
    },
    name: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    meta: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    token: {
      flex: 1,
      minWidth: 0,
      backgroundColor: isLight ? theme.palette.aeros.surface2 : "#0E1119",
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 8,
      padding: "7px 10px",
      fontFamily: MONO,
      fontSize: 12,
      letterSpacing: "0.08em",
      color: theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: { order: 1, flexBasis: "100%" },
    },
    actions: { display: "flex", gap: 5, flex: "none" },
    action: {
      width: 28,
      height: 28,
      borderRadius: 8,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "&:hover": { color: AEROS.cyan, borderColor: alpha(AEROS.cyan, 0.5) },
      "& .MuiSvgIcon-root": { fontSize: 15 },
      // Toque: alvo de 44px em tela de toque, sem mudar o desenho no mouse.
      "@media (pointer: coarse)": { width: 44, height: 44 },
    },
    smsSection: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      paddingBottom: 16,
      marginBottom: 4,
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
    },
    smsTitle: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.primary,
    },
    smsSubtitle: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.5 },
    smsEmpty: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 12,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    regenerate: {
      borderRadius: 8,
      textTransform: "none",
      fontSize: 12,
      fontWeight: 600,
      padding: "3px 10px",
      minHeight: 28,
      color: theme.palette.text.primary,
      borderColor: isLight ? theme.palette.divider : "rgba(255,255,255,0.16)",
      "&:hover": { color: AEROS.cyan, borderColor: alpha(AEROS.cyan, 0.5) },
      "@media (pointer: coarse)": { minHeight: 44 },
    },
    generate: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "7px 14px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
      minHeight: 36,
      "@media (pointer: coarse)": { minHeight: 44 },
      "&.Mui-disabled": { opacity: 0.6, color: "#07080D" },
    },
    empty: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      padding: "18px 0",
      textAlign: "center",
    },
    footer: {
      padding: "14px 24px",
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    footerHint: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "center",
      gap: 5,
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    closeButton: {
      marginLeft: "auto",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "8px 16px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
    },
    noToken: { color: stateText(theme, AEROS.warning, "warning"), letterSpacing: 0 },
  };
});

// "a7f3••••••••••••9c2e": as pontas bastam para reconhecer o token na lista
// sem expor o segredo em uma tela compartilhada.
export const maskToken = (token) => {
  if (!token) return "";
  if (token.length <= 12) return "•".repeat(token.length);
  return `${token.slice(0, 4)}${"•".repeat(20)}${token.slice(-4)}`;
};

const TokensModal = ({
  open,
  onClose,
  connections = [],
  onCopy,
  smsToken = null,
  isAdmin = false,
  onGenerateSmsToken,
}) => {
  const classes = useStyles();
  const [revealed, setRevealed] = useState({});
  const [smsVisivel, setSmsVisivel] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gerando, setGerando] = useState(false);

  // O token de SMS só é lido pelo admin (GET /sms/api-token responde 403 aos
  // demais): sem token conhecido e sem permissão, o bloco nem aparece.
  const mostraSms = isAdmin || !!smsToken;

  const gerarSms = async () => {
    if (!onGenerateSmsToken) return;
    setGerando(true);
    try {
      await onGenerateSmsToken();
    } finally {
      setGerando(false);
      setConfirmOpen(false);
      setSmsVisivel(false);
    }
  };

  const comToken = connections.filter((conn) => conn.token);

  const toggle = (id) =>
    setRevealed((atual) => ({ ...atual, [id]: !atual[id] }));

  const footer = (
    <div className={classes.footer}>
      <span className={classes.footerHint}>
        <LockOutlinedIcon />
        {i18n.t("aeros.api.tokens.footerHint")}
      </span>
      <Button className={classes.closeButton} onClick={onClose}>
        {i18n.t("aeros.api.tokens.close")}
      </Button>
    </div>
  );

  return (
    <AerosDialog
      open={open}
      onClose={onClose}
      width={720}
      title={i18n.t("aeros.api.tokens.title")}
      subtitle={i18n.t("aeros.api.tokens.subtitle")}
      footer={footer}
    >
      {mostraSms && (
        <div className={classes.smsSection}>
          <div>
            <div className={classes.smsTitle}>{i18n.t("aeros.api.tokens.sms.title")}</div>
            <div className={classes.smsSubtitle}>{i18n.t("aeros.api.tokens.sms.subtitle")}</div>
          </div>
          {smsToken ? (
            <div className={classes.row}>
              <div className={classes.token}>
                {smsVisivel ? smsToken : maskToken(smsToken)}
              </div>
              <div className={classes.actions}>
                <Tooltip
                  title={i18n.t(
                    smsVisivel ? "aeros.api.tokens.hide" : "aeros.api.tokens.reveal"
                  )}
                >
                  <IconButton
                    className={classes.action}
                    onClick={() => setSmsVisivel((v) => !v)}
                    aria-label={i18n.t(
                      smsVisivel ? "aeros.api.tokens.hide" : "aeros.api.tokens.reveal"
                    )}
                  >
                    {smsVisivel ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("aeros.api.tokens.copy")}>
                  <IconButton
                    className={classes.action}
                    onClick={() => onCopy(smsToken)}
                    aria-label={i18n.t("aeros.api.tokens.copy")}
                  >
                    <FileCopyOutlinedIcon />
                  </IconButton>
                </Tooltip>
                {isAdmin && (
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.regenerate}
                    startIcon={<RefreshIcon />}
                    onClick={() => setConfirmOpen(true)}
                    disabled={gerando}
                  >
                    {i18n.t("aeros.api.tokens.sms.regenerate")}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className={classes.smsEmpty}>
              <span>{i18n.t("aeros.api.tokens.sms.empty")}</span>
              {isAdmin && (
                <Button className={classes.generate} onClick={gerarSms} disabled={gerando}>
                  {gerando ? (
                    <CircularProgress size={16} style={{ marginRight: 8, color: "#07080D" }} />
                  ) : null}
                  {i18n.t("aeros.api.tokens.sms.generate")}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {comToken.length === 0 ? (
        <div className={classes.empty}>{i18n.t("aeros.api.tokens.empty")}</div>
      ) : (
        <div className={classes.list}>
          {comToken.map((conn) => {
            const online = conn.status === "CONNECTED";
            const oficial = conn.channel === "whatsapp_oficial";
            const canal = oficial
              ? i18n.t("aeros.api.channelOfficial")
              : i18n.t("aeros.api.channelBaileys");
            const visivel = !!revealed[conn.id];
            return (
              <div key={conn.id} className={classes.row}>
                <span
                  className={`${classes.dot} ${online ? classes.dotOn : classes.dotOff}`}
                />
                <div className={classes.identity}>
                  <div className={classes.name} title={conn.name}>
                    {conn.name}
                  </div>
                  <div className={classes.meta}>
                    {canal}
                    {conn.number ? ` · ${conn.number}` : ""}
                  </div>
                </div>
                <div className={classes.token}>
                  {visivel ? conn.token : maskToken(conn.token)}
                </div>
                <div className={classes.actions}>
                  <Tooltip
                    title={i18n.t(
                      visivel ? "aeros.api.tokens.hide" : "aeros.api.tokens.reveal"
                    )}
                  >
                    <IconButton
                      className={classes.action}
                      onClick={() => toggle(conn.id)}
                      aria-label={i18n.t(
                        visivel ? "aeros.api.tokens.hide" : "aeros.api.tokens.reveal"
                      )}
                    >
                      {visivel ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("aeros.api.tokens.copy")}>
                    <IconButton
                      className={classes.action}
                      onClick={() => onCopy(conn.token)}
                      aria-label={i18n.t("aeros.api.tokens.copy")}
                    >
                      <FileCopyOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <AerosConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={gerarSms}
        icon={<RefreshIcon />}
        tone="warning"
        title={i18n.t("aeros.api.tokens.sms.confirmTitle")}
        description={i18n.t("aeros.api.tokens.sms.confirmBody")}
        confirmLabel={i18n.t("aeros.api.tokens.sms.regenerate")}
        loading={gerando}
      />
    </AerosDialog>
  );
};

export default TokensModal;
